import { PillLabel } from "@/components/ui/pill-label";
import { canAccess } from "../providers/commons/canAccess";
import { useEffect, useRef, useState } from "react";
import "./workspace.css";
import { useGetIdentity } from "ra-core";
import { Link, useLocation } from "react-router";
import { ChevronDown } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { effectiveRole, isFullAccessRole } from "../sales/roles";
import { RoveroBrand } from "../root/RoveroBrand";
import { workspaceNavigation, routeGroup } from "./workspaceNavigation";
const sections: Record<string, { to: string; label: string }[]> = {
  counterparties: [
    { to: "/contacts", label: "Контакты" },
    { to: "/companies", label: "Компании" },
  ],
  management: [
    { to: "/deals", label: "Сделки" },
    { to: "/work-projects", label: "Проекты в работе" },
    { to: "/analytics", label: "Аналитика" },
  ],
};
export default function Header() {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  useEffect(() => setOpenGroup(null), [location.key]);
  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node))
        setOpenGroup(null);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);
  const { identity } = useGetIdentity();
  const role = effectiveRole(identity);
  const { pathname } = useLocation();
  let group = routeGroup(pathname);
  if (group === "projects" && isFullAccessRole(role)) group = "management";
  return (
    <header
      ref={headerRef}
      data-floating-header
      className="rovero-header"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setOpenGroup(null);
          (
            headerRef.current?.querySelector(
              `[data-group="${openGroup}"]`,
            ) as HTMLElement
          )?.focus();
        }
      }}
    >
      <div className="rovero-desktop-header-content mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3 px-4 py-4">
        <a
          href="#/"
          aria-label="ROVERO CRM — на главную и обновить"
          title="На главную и обновить"
          className="desktop-brand-pill"
          onClick={(event) => {
            if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
              return;
            event.preventDefault();
            window.location.hash = "/";
            window.location.reload();
          }}
        >
          <RoveroBrand />
        </a>
        <nav
          aria-label="Основная навигация"
          className="desktop-navigation-pill flex flex-wrap items-center gap-1"
        >
          {workspaceNavigation(role)
            .filter((i) => i.key !== "workspace")
            .map((item) => (
              <div
                className="workspace-hover-group relative"
                key={item.key}
                onMouseEnter={() =>
                  setOpenGroup(sections[item.key] ? item.key : null)
                }
                onMouseLeave={() => setOpenGroup(null)}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget))
                    setOpenGroup(null);
                }}
              >
                {sections[item.key] ? (
                  <button
                    type="button"
                    data-group={item.key}
                    aria-current={group === item.key ? "page" : undefined}
                    aria-expanded={openGroup === item.key}
                    aria-controls={`nav-${item.key}`}
                    onClick={() =>
                      setOpenGroup(openGroup === item.key ? null : item.key)
                    }
                    className={`rovero-nav-pill inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${group === item.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
                  >
                    <PillLabel>{item.label}</PillLabel>
                    <ChevronDown className="size-3" />
                  </button>
                ) : (
                  <Link
                    to={item.to}
                    aria-current={group === item.key ? "page" : undefined}
                    className={`rovero-nav-pill inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${group === item.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
                  >
                    <PillLabel>{item.label}</PillLabel>
                    {sections[item.key] && <ChevronDown className="size-3" />}
                  </Link>
                )}
                {sections[item.key] && openGroup === item.key && (
                  <div
                    id={`nav-${item.key}`}
                    className="workspace-hover-menu absolute left-0 top-full z-50 min-w-52 pt-2"
                  >
                    <nav
                      aria-label={`Разделы: $<PillLabel>{item.label}</PillLabel>`}
                      className="rounded-2xl border bg-popover p-2 shadow-xl"
                    >
                      {sections[item.key]
                        .filter((section) =>
                          canAccess(role, {
                            resource: section.to
                              .slice(1)
                              .replace("work-projects", "work_projects"),
                            action: "list",
                          }),
                        )
                        .map((i) => (
                          <Link
                            key={i.to}
                            onClick={() => setOpenGroup(null)}
                            to={i.to}
                            aria-current={
                              pathname === i.to ||
                              pathname.startsWith(i.to + "/")
                                ? "page"
                                : undefined
                            }
                            className="block rounded-xl px-4 py-3 text-sm text-popover-foreground hover:bg-muted focus-visible:bg-muted aria-[current=page]:bg-primary/10"
                          >
                            {i.label}
                          </Link>
                        ))}
                    </nav>
                  </div>
                )}
              </div>
            ))}
        </nav>
        <div className="desktop-actions-pill flex shrink-0 items-center gap-1">
          <ThemeModeToggle />
          <Link
            to="/workspace"
            aria-label="Личный кабинет"
            title="Личный кабинет"
            className="ml-1 rounded-full p-1 focus-visible:outline-2 focus-visible:outline-ring"
          >
            <Avatar className="size-9">
              <AvatarImage src={identity?.avatar} />
              <AvatarFallback>{identity?.fullName?.charAt(0)}</AvatarFallback>
            </Avatar>
          </Link>
        </div>
      </div>
    </header>
  );
}
